// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useState } from 'react';
import { IconButton, Badge, Typography, Popover, Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import downloadIcon from 'Assets/download-icon.png';
import uploadIcon from 'Assets/upload-kml-icon.png';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { ShowUploadSingleFileDialogAction, ShowOpsTrainingDialogAction } from 'Features/fileUpload/fileUploadActions';
import { SetOrderIdAction } from 'Features/order/orderActions';
import stylesDef from './styles';

interface ComponentProps {
  expand: boolean;
  checked:boolean;
  toggleExpand: () => void;
  clickDownload: (event: React.MouseEvent<HTMLElement>) => void;
  orderId: string;
  siteId: string;
  showFileDownloadState: string;
}

export const CadDrawingsButtons: React.FC<ComponentProps> = ({ checked, siteId, expand, toggleExpand, orderId, clickDownload, showFileDownloadState }) => {
  const styles = stylesDef();
  const dispatch = useAppDispatch();

  const downloadFileStatuses = useAppSelector((state) => state.order?.dxfFileStatus);
  const downloadGeoJsonFileStatuses = useAppSelector((state) => state.order?.geoJsonFileStatus);
  const anyChangeInFileStatuses = useAppSelector((state) => state.order.anyChangeInFileStatuses);
  const resources = useAppSelector((state) => state.auth.resources);
  const opsTrainer = 'opsTrainer' in resources;
  const isDemoProject = useAppSelector((state) => state.project?.project.isDemoProject);
  const [areDownloadsReady, setAreDownloadsReady] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  
  useEffect(() => {

    if (!downloadFileStatuses.length && !downloadGeoJsonFileStatuses.length) {
      setAreDownloadsReady(false);
      setIsDownloading(false);
    } else {
      // Check if ANY file has ongoing downloads
      const hasOngoingDownloads = [
        ...downloadFileStatuses,
        ...downloadGeoJsonFileStatuses
      ].some(file => 
        Object.values(file.fileReadyStatus).some(status => status === 0)
      );

      // Check if ALL files are ready (no ongoing downloads)
      const areAllFilesReady = !hasOngoingDownloads;
      
      setAreDownloadsReady(areAllFilesReady);
      setIsDownloading(hasOngoingDownloads);
    }
  }, [downloadFileStatuses, downloadGeoJsonFileStatuses, anyChangeInFileStatuses]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (isDownloading) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const DownloadButton = (
    <>
      <Badge color="primary" overlap="circular" variant="dot" invisible={!areDownloadsReady}>
        <IconButton
          aria-label="Download"
          sx={styles.button}
          onClick={clickDownload}
          title="Download files"
        >
          <img 
            src={downloadIcon} 
            alt="Download" 
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
          />
        </IconButton>
      </Badge>
      <Popover
        open={open}
        anchorEl={anchorEl}
        sx={styles.popover}
        aria-haspopup="true"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Box
          sx={styles.popoverText}
        >
          <Typography sx={styles.popoverTextLine}>A download is already in</Typography>
          <Typography sx={styles.popoverTextLine}>progress. Once ready, this icon</Typography>
          <Typography sx={styles.popoverTextLine}>will light up with a blue dot.</Typography>
        </Box>
      </Popover>
    </>
  );

  const uploadDxfClick = (e: React.ChangeEvent<{}>) => {
    dispatch(SetOrderIdAction(orderId));
    // eslint-disable-next-line no-unused-expressions
    opsTrainer ? dispatch(ShowOpsTrainingDialogAction()) : dispatch(ShowUploadSingleFileDialogAction());
    e.stopPropagation();
  };

  const uploadButton = (
    <IconButton
      aria-label="Upload"
      sx={styles.button}
      onClick={(e) => uploadDxfClick(e)}
      title="Upload .dxf"
    >
      <img src={uploadIcon} alt="Upload" />
    </IconButton>
  );

  const LayersExpandButton = (
    <IconButton sx={styles.button} onClick={toggleExpand}>
      {expand
        ? <KeyboardArrowDownIcon fontSize="small" />
        : <KeyboardArrowRightIcon fontSize="small" />}
    </IconButton>
  );

  return (
    <>
      {uploadButton}
      {(!isDemoProject && !opsTrainer) && DownloadButton}
      {checked && LayersExpandButton}
    </>
  );
};

export default CadDrawingsButtons;
